import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { authClient, isWorkspaceSelection, type SessionState } from '@/services/authClient';
import { onAccessTokenRefreshFailed } from '@/services/sessionRefresh';
import { disconnectSocket } from '@/services/wsClient';
import { isCurrentSessionOrigin } from '@/services/sessionOrigin';
import { AuthContext, type AuthContextValue } from './authContext';

const initialSession: SessionState = {
  authenticated: false,
  user: null,
  tenant: null,
  roles: [],
  permissions: [],
  message: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionState>(initialSession);
  const [isLoading, setIsLoading] = useState(true);
  const operation = useRef(0);
  const mounted = useRef(false);
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const isCurrentOperation = useCallback((ownOperation: number) =>
    mounted.current && ownOperation === operation.current, []);
  const replaceSession = useCallback((next: SessionState) => {
    if (!mounted.current) return;
    if (next.accessMode === 'PASSWORD_CHANGE_REQUIRED' || next.accessMode === 'COMPANY_SETUP') disconnectSocket();
    sessionRef.current = next;
    setSession(next);
  }, []);

  const reloadSession = useCallback(async () => {
    const ownOperation = ++operation.current;
    if (mounted.current) setIsLoading(true);
    try {
      const next = await authClient.getSession();
      if (isCurrentOperation(ownOperation)) replaceSession(next);
      return next;
    } finally {
      if (isCurrentOperation(ownOperation)) setIsLoading(false);
    }
  }, [isCurrentOperation, replaceSession]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // Invalidate pending work, including StrictMode's first effect setup.
      operation.current += 1;
    };
  }, []);

  useEffect(() => {
    void reloadSession();
  }, [reloadSession]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (!mounted.current || event.key !== 'helios_access_token') return;
      disconnectSocket();
      replaceSession(initialSession);
      void reloadSession();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [reloadSession, replaceSession]);

  useEffect(() => {
    return onAccessTokenRefreshFailed(() => {
      if (!mounted.current) return;
      operation.current += 1;
      disconnectSocket();
      replaceSession(initialSession);
      setIsLoading(false);
    });
  }, [replaceSession]);

  const signIn = useCallback<AuthContextValue['signIn']>(async (input) => {
    const ownOperation = ++operation.current;
    if (mounted.current) setIsLoading(true);
    try {
      const next = await authClient.login(input);
      // A workspace choice is not a session; leaving provider state untouched
      // keeps the app unauthenticated until one is actually picked.
      if (isCurrentOperation(ownOperation) && !isWorkspaceSelection(next)) {
        replaceSession(next);
      }
      return next;
    } finally {
      if (isCurrentOperation(ownOperation)) setIsLoading(false);
    }
  }, [isCurrentOperation, replaceSession]);

  const signInDemo = useCallback<AuthContextValue['signInDemo']>(async () => {
    const ownOperation = ++operation.current;
    if (mounted.current) setIsLoading(true);
    try {
      const next = await authClient.loginDemo();
      if (isCurrentOperation(ownOperation)) replaceSession(next);
      return next;
    } finally {
      if (isCurrentOperation(ownOperation)) setIsLoading(false);
    }
  }, [isCurrentOperation, replaceSession]);

  const signInWithPasskey = useCallback<AuthContextValue['signInWithPasskey']>(
    async (tenantSlug) => {
      const ownOperation = ++operation.current;
      if (mounted.current) setIsLoading(true);
      try {
        const next = await authClient.loginWithPasskey(tenantSlug);
        if (isCurrentOperation(ownOperation)) replaceSession(next);
        return next;
      } finally {
        if (isCurrentOperation(ownOperation)) setIsLoading(false);
      }
    },
    [isCurrentOperation, replaceSession],
  );

  const enrollPasskey = useCallback<AuthContextValue['enrollPasskey']>(async () => {
    const ownOperation = operation.current;
    if (mounted.current) setIsLoading(true);
    try {
      return await authClient.enrollPasskey();
    } finally {
      if (isCurrentOperation(ownOperation)) setIsLoading(false);
    }
  }, [isCurrentOperation]);

  const signOut = useCallback(async () => {
    const ownOperation = ++operation.current;
    disconnectSocket();
    replaceSession(initialSession);
    if (mounted.current) setIsLoading(true);
    try {
      await authClient.logout();
      if (isCurrentOperation(ownOperation)) replaceSession(initialSession);
    } finally {
      if (isCurrentOperation(ownOperation)) setIsLoading(false);
    }
  }, [isCurrentOperation, replaceSession]);

  const applyOwnProfile = useCallback<AuthContextValue['applyOwnProfile']>((origin, profile) => {
    const current = sessionRef.current;
    if (
      !mounted.current || !current.authenticated || !current.user || !current.tenant || current.accessMode === 'PASSWORD_CHANGE_REQUIRED' ||
      !isCurrentSessionOrigin(origin.sessionOrigin) ||
      current.tenant?.id !== origin.tenantId || current.user?.id !== origin.userId ||
      profile.id !== current.user.id ||
      !Number.isInteger(profile.version) || profile.version < 1 ||
      (current.user.version !== undefined && profile.version < current.user.version) ||
      JSON.stringify([...new Set(current.permissions)].sort()) !== origin.permissions
    ) return false;
    operation.current += 1;
    replaceSession({ ...current, user: { ...current.user, name: profile.name, title: profile.title, version: profile.version } });
    setIsLoading(false);
    return true;
  }, [replaceSession]);

  const completePasswordChange = useCallback<AuthContextValue['completePasswordChange']>(async (input) => {
    const ownOperation = ++operation.current;
    disconnectSocket();
    const next = await authClient.changePassword(input);
    if (!isCurrentOperation(ownOperation)) throw new Error('Your sign-in changed. Continue with the current session.');
    replaceSession(next);
    return next;
  }, [isCurrentOperation, replaceSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...session,
      isLoading,
      signIn,
      signInDemo,
      signInWithPasskey,
      enrollPasskey,
      signOut,
      reloadSession,
      applyOwnProfile,
      completePasswordChange,
    }),
    [
      enrollPasskey,
      isLoading,
      reloadSession,
      applyOwnProfile,
      completePasswordChange,
      session,
      signIn,
      signInDemo,
      signInWithPasskey,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
